import React, { useEffect, useState } from "react";
import ModalShell from "./ModalShell";
import { isValidYyyyMmDd } from "../shared/utils/calendarYmd";
import {
  submitClosestFormOnDateInputKeyUp,
  submitClosestFormOnEnterKeyDown,
} from "../shared/utils/fieldCommitOnEnterKeyDown";

/**
 * Focused form for creating an active project from either an account or System template.
 * The parent owns the API call and redirects after a successful save.
 */
function ProjectFromTemplateModal({
  open,
  templateName,
  initialName,
  initialStartDate,
  onCancel,
  onCreate,
}) {
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(initialName || "");
    setStartDate(initialStartDate || "");
    setSubmitting(false);
    setError("");
  }, [open, initialName, initialStartDate]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting || !onCreate) return;

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Project name is required.");
      return;
    }
    if (!isValidYyyyMmDd(startDate)) {
      setError("Start date is required and must be a valid date.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onCreate({ name: trimmedName, start_date: startDate });
    } catch (err) {
      setError(err?.message || "Could not create project from template.");
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      open={open}
      onClose={submitting ? undefined : onCancel}
      title="Create project from template"
      subtitle={templateName ? `Template: ${templateName}` : undefined}
      maxWidth={520}
      closeOnEscape={!submitting}
      closeOnOverlayClick={!submitting}
      footer={
        <>
          <button type="button" className="btn btn-md btn-secondary" disabled={submitting} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            form="project-from-template-form"
            className="btn btn-md btn-primary"
            disabled={submitting || !name.trim() || !startDate}
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <form id="project-from-template-form" onSubmit={handleSubmit}>
        <label style={{ display: "block", marginBottom: 14 }}>
          <span className="form-label">Project name</span>
          <input
            type="text"
            className="input input-full"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={submitClosestFormOnEnterKeyDown}
            disabled={submitting}
            autoFocus
          />
        </label>
        <label style={{ display: "block" }}>
          <span className="form-label">Start date</span>
          <input
            type="date"
            className="input input-full"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            onKeyDown={submitClosestFormOnEnterKeyDown}
            onKeyUp={submitClosestFormOnDateInputKeyUp}
            disabled={submitting}
            required
          />
        </label>
        {error ? (
          <div role="alert" style={{ marginTop: 12, color: "#b42318", fontSize: 13 }}>
            {error}
          </div>
        ) : null}
      </form>
    </ModalShell>
  );
}

export default ProjectFromTemplateModal;
